import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, ArrowUpRight, MapPin, BookOpen } from "lucide-react";
import { CITY_GUIDES } from "../data";
import { Navbar } from "../components/Navbar";
import { AreaAlert } from "../components/AreaAlert";
import { LeadMagnet } from "../components/LeadMagnet";
import { Contact, Footer } from "../components/Contact";
import { Reveal } from "../components/Reveal";
import { setSeo } from "../lib/seo";

export default function CityGuide({ slug }) {
  const city = CITY_GUIDES.find((c) => c.slug === slug);

  useEffect(() => {
    if (city) {
      setSeo({
        title: `Moving to ${city.name}, CA | Relocation Guide — Melissa O'Donnell`,
        description: `Thinking about moving to ${city.name}, CA? A local relocation guide to home value, schools, lifestyle, and commute for buyers relocating to the Inland Empire.`
      });
    }
    window.scrollTo(0, 0);
  }, [city]);

  if (!city) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-heading text-4xl font-light">City not found</h1>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-gold hover:underline" data-testid="city-back-home">
          <ArrowLeft size={16} /> Back home
        </Link>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <main data-testid={`city-guide-${city.slug}`}>
        {/* Hero */}
        <section className="relative min-h-[78vh] flex items-end">
          <div className="absolute inset-0">
            <img src={city.image} alt={`Homes in ${city.name}, ${city.state}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/50" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/20" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 w-full">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow text-gold mb-5">
              Relocation Guide · {city.state}
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="font-heading font-light text-white text-5xl sm:text-7xl tracking-tight leading-none">
              Moving to {city.name}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }} className="text-white/85 text-lg font-light max-w-2xl mt-6">
              {city.blurb}
            </motion.p>
          </div>
        </section>

        {/* Stats bar */}
        <section className="bg-[#0a0a0a] border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {city.stats.map((s) => (
              <div key={s.label} className="py-7 px-4 text-center" data-testid={`city-stat-${s.label.toLowerCase()}`}>
                <p className="eyebrow text-[10px] text-gold mb-2">{s.label}</p>
                <p className="text-white font-heading text-lg leading-tight">{s.value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Overview + why move */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="eyebrow text-gold mb-4">Why buyers relocate to {city.name}</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-light leading-tight">The honest local take</h2>
                <p className="text-[#52525b] font-light text-lg mt-6 leading-relaxed">{city.intro}</p>
                <p className="text-[#52525b] font-light text-lg mt-5 leading-relaxed">{city.lifestyle}</p>

                <div className="mt-8 bg-surface border-l-2 border-gold p-6">
                  <p className="eyebrow text-[11px] text-[#a1a1aa] mb-2">Worth knowing before you buy</p>
                  <p className="text-[#3f3f46] font-light leading-relaxed">{city.consider}</p>
                </div>

                <div className="mt-10 pt-8 border-t border-black/5" data-testid="city-nearby">
                  <p className="eyebrow text-[11px] text-[#a1a1aa] mb-4">Also serving nearby communities</p>
                  <div className="flex flex-wrap gap-2.5">
                    {city.nearby.map((n) => (
                      <span key={n} className="inline-flex items-center gap-1.5 border border-black/10 px-3.5 py-1.5 text-sm font-light hover:border-gold transition-colors">
                        <MapPin size={13} className="text-gold" /> {n}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="bg-surface border border-black/5 p-8">
                  <h3 className="font-heading text-2xl">Why families love {city.name}</h3>
                  <ul className="mt-6 space-y-4">
                    {city.whyMove.map((h) => (
                      <li key={h} className="flex items-start gap-3 font-light">
                        <Check size={16} className="text-gold shrink-0 mt-1" /> {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Lead magnet */}
        <LeadMagnet />

        {/* Related reading */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-8">
              <BookOpen className="text-gold" size={20} />
              <h3 className="font-heading text-2xl">More on {city.name} & the Inland Empire</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {city.relatedPosts.map((p) => (
                <Reveal key={p.slug}>
                  <Link to={`/blog/${p.slug}`} data-testid={`city-related-${p.slug}`} className="group block bg-surface border border-black/5 p-6 h-full hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                    <span className="eyebrow text-[10px] text-gold">Guide</span>
                    <p className="font-heading text-lg mt-2 leading-snug group-hover:text-gold transition-colors">{p.title}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm mt-4 text-[#0a0a0a] group-hover:text-gold transition-colors">Read <ArrowUpRight size={14} /></span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Buyer alert */}
        <section className="py-20 md:py-28 bg-[#0a0a0a]">
          <div className="max-w-5xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <p className="eyebrow text-gold mb-4">Buyer Alerts</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-light leading-tight text-white">
                New {city.name} homes, straight to your inbox
              </h2>
              <p className="text-white/70 font-light text-lg mt-6 leading-relaxed">
                Set it and forget it. We'll alert you the instant a home matching {city.name} hits the market.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white mt-8 text-sm transition-colors" data-testid="city-back">
                <ArrowLeft size={16} /> Explore all areas
              </Link>
            </Reveal>
            <Reveal delay={0.15}>
              <AreaAlert fixedArea={`${city.name}, ${city.state}`} dark />
            </Reveal>
          </div>
        </section>

        <Contact />
      </main>
      <Footer />
    </>
  );
}
